"use client";

import { BusinessEntrySkeleton } from "@/app/(saas)/(app)/_components/business-entry-skeleton";
import { BusinessFilters } from "@/app/(saas)/(app)/businesses/_components/business-filters";
import { BusinessListBreadcrumbs } from "@/app/(saas)/(app)/businesses/_components/business-list-breadcrumbs";
import { BusinessListNearbySection } from "@/app/(saas)/(app)/businesses/_components/business-list-nearby-section";
import { BusinessListZeroDataState } from "@/app/(saas)/(app)/businesses/_components/business-list-zero-data-state";
import { BusinessesMobileHeader } from "@/app/(saas)/(app)/businesses/_components/businesses-mobile-header";
import { LocationSearchBox } from "@/app/(saas)/(app)/businesses/_components/filters/location-search-box";
import { MapListingCard } from "@/app/(saas)/(app)/businesses/_components/map-listing-card";
import { ListingMapMarker, ListingMapPopup } from "./listing-map-marker";
import { MapAreaMarker } from "./map-area-marker";
import { MapAreaPopup } from "./map-area-popup";
import { MapUnpinnedControl } from "./map-unpinned-control";
import { isAreaMapPoint, type SearchMapPoint } from "./search-map-points";
import { useMapAreaShades } from "./use-map-area-shades";
import {
  type BusinessFilters as BusinessFiltersType,
  businessFiltersAtom,
  businessMapBoundsAtom,
  readFromSearchParams,
  updateSearchParams,
  URL_KEYS,
} from "@/app/(saas)/(app)/businesses/_components/filters/state";
import { ListingPreview } from "@/components/listings/listing-preview";
import { Button } from "@/components/ui/button";
import { H1 } from "@/components/ui/heading";
import { useCurrentUser } from "@/hooks/use-current-user";
import { LoaderCircleIcon, MapPinIcon, XIcon } from "lucide-react";
import Link from "next/link";
import { api } from "@/lib/api-client";
import {
  buildLocationUrlSegments,
  cachedEstimateLocationFromMap,
} from "@/lib/locations";
import type {
  ListingsQueryFilters,
  ListingsOrderByDirection,
  ListingsOrderByProperty,
  SearchData,
} from "@/lib/types";
import { buildListingUrl } from "@/lib/url";
import { cn } from "@/lib/utils";
import {
  getMapComponents,
  useMapClustering,
  type AreaShadeMap,
  type MapClient,
  type MapCoords,
  type SearchHookLocation,
} from "@repo/maps";
import { useAtom } from "jotai";
import { useSetAtom } from "jotai";
import { useTranslations } from "next-intl";
import { usePathname, useSearchParams } from "next/navigation";
import { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import useInfiniteScroll from "react-infinite-scroll-hook";
import { BusinessesBreadcrumbsJsonLD } from "@/app/(saas)/(app)/businesses/_components/businesses-breadcrumbs-json-ld";
import { BusinessesJsonLD } from "@/app/(saas)/(app)/businesses/_components/businesses-json-ld";
import { SaveSearchBanner } from "@/app/(saas)/(app)/businesses/_components/save-search-banner";
import { buildListingFilterParams, type ListingInfoAttribute } from "@repo/utils";
import { keepPreviousData } from "@tanstack/react-query";
import { useMediaQuery } from "usehooks-ts";
import { mediaQuery } from "@/lib/media-query";
import { useScrollHide } from "@/hooks/use-scroll-hide";
import { useScrollRestore } from "@/hooks/use-scroll-restore";
import type { OrderByValue, SavedListingsCache } from "./businesses-view-types";
import {
  formatLocationWithFallback,
  formatLocationForZoom,
  parseOrderByValue,
  getSafeBounds,
  routeLocationPath,
  shouldAdoptRouteLocation,
  ORDER_BY_VALUES,
} from "./businesses-view-helpers";
import {
  MAP_BOUNDS_STORAGE_KEY,
  LISTINGS_CACHE_KEY,
  getSavedMapBounds,
  saveMapBounds,
  getSavedLocation,
  saveLocation,
  saveListingsCache,
  getListingsCache,
} from "./businesses-view-cache";

export type BusinessesSearchView = "map" | "list";

export type BusinessesViewProps = {
  startingCountryCode?: string;
  startingLocation?: SearchHookLocation;
  coords?: MapCoords;
};

// Survives client-side (SPA) navigations but resets on full page reload
// because JS module memory is wiped. Used to distinguish "client-side
// back-nav" from "hard reload" — sessionStorage alone can't tell them apart.
let businessesPageHasMounted = false;

export default function BusinessesView({
  startingCountryCode,
  startingLocation,
  coords,
}: BusinessesViewProps) {
  const searchParams = useSearchParams();
  const pathname = usePathname();

  const isNotMobile = useMediaQuery(mediaQuery.sm);
  const isDesktopLayout = useMediaQuery(mediaQuery.xl);

  const mapRef = useRef<MapClient>(null);
  const locationCacheRef = useRef(new Map<string, SearchHookLocation>());

  const { MapComponent, useMapSearch } = getMapComponents<SearchMapPoint>(
    process.env.NEXT_PUBLIC_MAPS_PROVIDER as any,
  );

  const mapSearchHook = useMapSearch({
    token: process.env.NEXT_PUBLIC_MAP_TOKEN as string,
  });

  const { user } = useCurrentUser();

  // Resolve the actual starting location synchronously at render time.
  // Next.js may serve a stale cached page (e.g. California) when the user
  // navigates back after changing the location (e.g. to Miami). In that
  // case the sessionStorage location is the one the user last saw.
  const resolved = useRef(
    (() => {
      const fallback = {
        location: startingLocation ?? null,
        countryCode: startingCountryCode,
        fromSaved: false,
      };

      if (typeof window === "undefined") return fallback;
      const saved = getSavedLocation();
      if (!saved) return fallback;

      const currentPath = window.location.pathname;
      const savedResult = {
        location: saved.location,
        countryCode: saved.countryCode ?? startingCountryCode,
        fromSaved: true,
      };

      if (currentPath.startsWith("/businesses") && saved.url === currentPath) {
        return savedResult;
      }

      if (currentPath === "/businesses" && window.location.search !== "") {
        return savedResult;
      }

      return fallback;
    })(),
  );

  // On back-navigation (location restored from sessionStorage), also restore
  // the saved map bounds so queries run immediately with cached data and the
  // scroll position can be restored without waiting for the map to settle.
  const savedBounds = useRef(
    resolved.current.location && !resolved.current.fromSaved
      ? null
      : getSavedMapBounds(),
  );

  const cachedListings = useRef(
    resolved.current.fromSaved ? getListingsCache() : null,
  );

  // Start null to match the server render (avoids hydration mismatch).
  // Populated in useLayoutEffect below — runs before the browser paints,
  // so the user never sees skeletons on back-nav.
  const [backNavCache, setBackNavCache] = useState<SavedListingsCache | null>(
    null,
  );

  // All sessionStorage-derived state starts as null so the initial client
  // render matches the server (which has no access to sessionStorage).
  // The useLayoutEffect below hydrates them before the first paint.
  const [swLat, setSwLat] = useState<number | null>(null);
  const [swLng, setSwLng] = useState<number | null>(null);
  const [neLat, setNeLat] = useState<number | null>(null);
  const [neLng, setNeLng] = useState<number | null>(null);
  const [currentZoom, setCurrentZoom] = useState(12);

  const [fetchSwLat, setFetchSwLat] = useState<number | null>(null);
  const [fetchSwLng, setFetchSwLng] = useState<number | null>(null);
  const [fetchNeLat, setFetchNeLat] = useState<number | null>(null);
  const [fetchNeLng, setFetchNeLng] = useState<number | null>(null);

  // Hydration-safe: restore all sessionStorage state after hydration but
  // before paint. Batched into a single useLayoutEffect so React only
  // triggers one synchronous re-render.
  useLayoutEffect(() => {
    // Only restore cached listings overlay on actual back/forward navigation
    if (isBackNavigation.current && cachedListings.current) {
      setBackNavCache(cachedListings.current);
    }

    // Always restore bounds from sessionStorage (even on refresh) so queries
    // fire immediately rather than waiting for the map to settle and report
    // its viewport. Without this, a refresh with filters shows a blank page.
    const sb = savedBounds.current;
    if (sb) {
      setSwLat(sb.sw.lat);
      setSwLng(sb.sw.lng);
      setNeLat(sb.ne.lat);
      setNeLng(sb.ne.lng);
      setFetchSwLat(sb.fetchSw.lat);
      setFetchSwLng(sb.fetchSw.lng);
      setFetchNeLat(sb.fetchNe.lat);
      setFetchNeLng(sb.fetchNe.lng);
    } else if (resolved.current.location?.bbox) {
      const safeBounds = getSafeBounds(
        resolved.current.location.bbox,
        resolved.current.location.coords,
      );
      if (safeBounds) {
        setSwLat(safeBounds.sw.lat);
        setSwLng(safeBounds.sw.lng);
        setNeLat(safeBounds.ne.lat);
        setNeLng(safeBounds.ne.lng);

        const latSpan = safeBounds.ne.lat - safeBounds.sw.lat;
        const lngSpan = safeBounds.ne.lng - safeBounds.sw.lng;
        const latPad = latSpan * 0.5;
        const lngPad = lngSpan * 0.5;
        setFetchSwLat(Math.max(safeBounds.sw.lat - latPad, -85));
        setFetchSwLng(Math.max(safeBounds.sw.lng - lngPad, -180));
        setFetchNeLat(Math.min(safeBounds.ne.lat + latPad, 85));
        setFetchNeLng(Math.min(safeBounds.ne.lng + lngPad, 180));
      }
    }
  }, []);

  const t = useTranslations();

  const [filters, setFilters] = useAtom(businessFiltersAtom);
  const setMapBounds = useSetAtom(businessMapBoundsAtom);

  const [countryCode, setCountryCode] = useState<string | undefined>(
    resolved.current.countryCode,
  );
  const [inferredLocation, setInferredLocation] =
    useState<SearchHookLocation | null>(null);

  const [shouldKeepLocation, setShouldKeepLocation] = useState(true);
  const keepLocationCountRef = useRef(2);
  const isInitialLocationFromServer = useRef(!!resolved.current.location);
  const adoptedRoutePath = useRef<string | null>(null);
  const isBackNavigation = useRef(
    typeof window !== "undefined" &&
      (() => {
        const navEntry = window.performance?.getEntriesByType?.(
          "navigation",
        )?.[0] as PerformanceNavigationTiming | undefined;
        if (navEntry?.type === "back_forward") return true;
        // Module-level flag: true means the component already mounted once
        // in this JS session (i.e. this is a client-side SPA navigation back
        // to the page). On a full reload JS memory is wiped so the flag is
        // false, correctly preventing stale cache / scroll restoration.
        return businessesPageHasMounted;
      })(),
  );
  // Mount-phase settling window. Multiple useEffects set filters during mount
  // (URL search params, location restore) which recompute filterParams and
  // re-trigger the [filterParams] effect. The timestamp lets the effect
  // distinguish mount-phase re-fires from real user filter changes.
  // On back-nav the window is longer to cover async settling.
  const backNavMountedAt = useRef(
    typeof window !== "undefined" ? Date.now() : 0,
  );

  // Stores the serialized previous filterParams so the reset effect can
  // compare by value and only run when the filters truly change.
  const prevFilterParamsRef = useRef<string | null>(null);

  useEffect(() => {
    businessesPageHasMounted = true;
  }, []);

  const [searchView, setSearchView] = useState<BusinessesSearchView>("list");

  const [navigatingSlug, setNavigatingSlug] = useState<string | null>(null);

  const [selectedMapListingSlug, setSelectedMapListingSlug] = useState<
    string | null
  >(null);
  const [selectedAreaId, setSelectedAreaId] = useState<number | null>(null);
  const [areaFocus, setAreaFocus] = useState<{
    subdivisionId: number;
    name: string;
  } | null>(null);
  const areaFocusRef = useRef(areaFocus);
  areaFocusRef.current = areaFocus;
  const ignoreAreaFocusClearRef = useRef(false);
  const [hoveredAreaId, setHoveredAreaId] = useState<number | null>(null);
  const [areaShadeMap, setAreaShadeMap] = useState<AreaShadeMap | null>(null);
  const [isLocatingUser, setIsLocatingUser] = useState(false);
  const [scoutSetupError, setScoutSetupError] = useState<string | null>(null);

  const [orderByProperty, setOrderByProperty] =
    useState<ListingsOrderByProperty>("recommended");
  const [orderByDirection, setOrderByDirection] =
    useState<ListingsOrderByDirection>("desc");

  const [orderByValue, setOrderByValue] =
    useState<OrderByValue>("recommended_desc");

  useEffect(() => {
    const { property, direction } = parseOrderByValue(orderByValue);
    setOrderByProperty(property);
    setOrderByDirection(direction);
  }, [orderByValue]);

  const filterParams = useMemo(
    () => buildListingFilterParams(filters),
    [
      filters.businessTypeIds,
      filters.statuses,
      filters.listedBy,
      filters.saleTypes,
      filters.priceRange,
      filters.revenueRange,
      filters.profitRange,
      filters.sdeRange,
      filters.sdeMultipleRange,
      filters.revenueMultipleRange,
      filters.downPaymentRange,
      filters.establishmentAgeRange,
      filters.employeeCountRange,
      filters.ownerWorkedHoursRange,
      filters.propertyIncluded,
      filters.financingAvailable,
      filters.sbaPrequalified,
      filters.visaQualified,
      filters.includeMissingMultiples,
      filters.priceDirection,
      filters.priceChangedWithinDays,
      filters.minPriceChangePercent,
    ],
  );

  // Map markers use the wider padded fetch bounds so markers persist during
  // small pan/zoom without re-fetching.
  const mapQuery = useMemo<ListingsQueryFilters>(
    () => ({
      ...filterParams,
      countryCode: countryCode?.toUpperCase(),
      bbox:
        fetchSwLat && fetchSwLng && fetchNeLat && fetchNeLng
          ? {
              sw: { lat: fetchSwLat, lng: fetchSwLng },
              ne: { lat: fetchNeLat, lng: fetchNeLng },
            }
          : undefined,
    }),
    [fetchSwLat, fetchSwLng, fetchNeLat, fetchNeLng, filterParams, countryCode],
  );

  // List and count use the tight viewport bounds so they only reflect listings
  // whose markers are actually visible on screen.
  const viewportQuery = useMemo<ListingsQueryFilters>(
    () => ({
      ...filterParams,
      countryCode: countryCode?.toUpperCase(),
      bbox:
        swLat !== null && swLng !== null && neLat !== null && neLng !== null
          ? { sw: { lat: swLat, lng: swLng }, ne: { lat: neLat, lng: neLng } }
          : undefined,
    }),
    [swLat, swLng, neLat, neLng, filterParams, countryCode],
  );

  const hasMapBounds =
    fetchSwLat !== null &&
    fetchSwLng !== null &&
    fetchNeLat !== null &&
    fetchNeLng !== null;
  const hasViewportBounds =
    swLat !== null && swLng !== null && neLat !== null && neLng !== null;

  const { data: countData, isLoading: countDataLoading } =
    api.listings.count.useQuery(viewportQuery, {
      enabled: hasViewportBounds,
      placeholderData: (prev) => prev,
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
    });

  const { data: mapSearchData, isFetching: isMapFetching } =
    api.listings.getMapListings.useQuery(mapQuery, {
      enabled: hasMapBounds,
      placeholderData: (prev) => prev,
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
    });

  const {
    data,
    isLoading,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = api.listings.get.useInfiniteQuery(
    {
      ...viewportQuery,
      ...(areaFocus
        ? { areaSubdivisionId: areaFocus.subdivisionId }
        : {}),
      orderByProperty,
      orderByDirection,
    },
    {
      enabled: hasViewportBounds,
      placeholderData: keepPreviousData,
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
      getNextPageParam: (lastPage: {
        nextCursor?:
          | {
              tier: "PINNED" | "AREA_IN_VIEW" | "WIDER" | "LOCAL" | "AREA";
              id: number;
            }
          | number
          | null;
      }) => lastPage.nextCursor,
    },
  );

  const clusterBounds = useMemo(() => {
    if (fetchSwLat === null || fetchSwLng === null || fetchNeLat === null || fetchNeLng === null) return null;
    return {
      sw: { lat: fetchSwLat, lng: fetchSwLng },
      ne: { lat: fetchNeLat, lng: fetchNeLng },
    };
  }, [fetchSwLat, fetchSwLng, fetchNeLat, fetchNeLng]);

  const mapListings = useMemo(
    () =>
      (mapSearchData?.listings ?? []).filter(
        (item) =>
          item.business?.address?.latitude != null &&
          item.business?.address?.longitude != null,
      ),
    [mapSearchData?.listings],
  );

  const { clusters: clusteredMapData, getClusterExpansionZoom } =
    useMapClustering(
      mapListings,
      (item) => ({
        lat: item.business!.address!.latitude!,
        lng: item.business!.address!.longitude!,
      }),
      clusterBounds,
      currentZoom,
    );

  const mapPoints = useMemo<SearchMapPoint[]>(() => {
    const areas: SearchMapPoint[] = (mapSearchData?.areas ?? []).map((area) => ({
      kind: "area",
      area,
      lat: area.center.lat,
      lng: area.center.lng,
    }));
    return [...clusteredMapData, ...areas];
  }, [clusteredMapData, mapSearchData?.areas]);

  const onAreaHover = useCallback(
    (action: { type: "enter" | "leave"; id: number }) => {
      setHoveredAreaId((current) => {
        if (action.type === "enter") return action.id;
        return current === action.id ? null : current;
      });
    },
    [],
  );

  useMapAreaShades(
    areaShadeMap,
    mapSearchData?.areas,
    clusterBounds,
    hoveredAreaId,
  );

  const [infiniteRef, { rootRef }] = useInfiniteScroll({
    loading: isLoading || isFetchingNextPage,
    hasNextPage,
    onLoadMore: fetchNextPage,
  });

  const { shouldHide: hideMobileHeaderOnScroll, onScroll: onListScroll } =
    useScrollHide({
      enabled: !isDesktopLayout && searchView === "list",
    });

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const areaSectionRef = useRef<HTMLDivElement>(null);
  const areaCount = mapSearchData?.areaCount ?? 0;
  const selectedArea =
    mapSearchData?.areas.find((area) => area.subdivisionId === selectedAreaId) ??
    null;

  const focusAreaListings = useCallback(
    (area: { subdivisionId: number; name: string }) => {
      ignoreAreaFocusClearRef.current = true;
      window.setTimeout(() => {
        ignoreAreaFocusClearRef.current = false;
      }, 1000);
      setAreaFocus(area);
      if (!isDesktopLayout) setSearchView("list");
    },
    [isDesktopLayout],
  );

  const showAreaListings = useCallback(() => {
    if (!isDesktopLayout) setSearchView("list");
    const scroll = () => {
      const container = scrollContainerRef.current;
      const section = areaSectionRef.current;
      if (container && section) {
        const top =
          section.getBoundingClientRect().top -
          container.getBoundingClientRect().top +
          container.scrollTop;
        container.scrollTo({ top, behavior: "smooth" });
        return;
      }
      container?.scrollTo({ top: container.scrollHeight, behavior: "smooth" });
    };
    if (isDesktopLayout) {
      scroll();
      return;
    }
    window.setTimeout(scroll, 50);
  }, [isDesktopLayout]);

  const scrolledAreaFocusRef = useRef<number | null>(null);
  useEffect(() => {
    if (!areaFocus) {
      scrolledAreaFocusRef.current = null;
      return;
    }
    if (isFetching && !isFetchingNextPage) return;
    if (scrolledAreaFocusRef.current === areaFocus.subdivisionId) return;

    const scroll = () => {
      const container = scrollContainerRef.current;
      const section = areaSectionRef.current;
      if (!container || !section) return false;
      const top =
        section.getBoundingClientRect().top -
        container.getBoundingClientRect().top +
        container.scrollTop;
      container.scrollTo({ top, behavior: "smooth" });
      scrolledAreaFocusRef.current = areaFocus.subdivisionId;
      return true;
    };

    if (scroll()) return;
    const timer = window.setTimeout(scroll, 50);
    return () => window.clearTimeout(timer);
  }, [areaFocus, data, isFetching, isFetchingNextPage]);

  const rootRefLatest = useRef(rootRef);
  rootRefLatest.current = rootRef;

  const mergedListRef = useCallback(
    (node: HTMLDivElement | null) => {
      rootRefLatest.current(node);
      (
        scrollContainerRef as React.MutableRefObject<HTMLDivElement | null>
      ).current = node;
    },
    [],
  );

  const {
    onScroll: onScrollRestore,
    clearSavedPosition,
    saveNow: saveScrollNow,
  } = useScrollRestore("businesses-scroll", scrollContainerRef, {
    restore: isBackNavigation.current,
  });

  const handleListScroll = useCallback(
    (e: React.UIEvent<HTMLElement>) => {
      onListScroll(e);
      onScrollRestore(e);
    },
    [onListScroll, onScrollRestore],
  );

  const createSearchMutation = api.searches.create.useMutation();

  function onLocationChange(
    newLocation: SearchHookLocation,
    options?: { animate?: boolean },
  ) {
    if (newLocation.bbox) {
      const bounds = getSafeBounds(newLocation.bbox, newLocation.coords);
      if (bounds) {
        mapRef.current?.setBounds(bounds, { animate: options?.animate });
      } else if (newLocation.coords) {
        mapRef.current?.goToCoords(newLocation.coords);
      }
    } else if (newLocation.coords) {
      mapRef.current?.goToCoords(newLocation.coords);
    }

    const newCountryCode = newLocation.context.country?.code?.toLowerCase();
    if (newCountryCode) {
      setCountryCode(newCountryCode);
    }
  }

  function updateFetchBoundsIfNeeded(bounds: {
    sw: { lat: number; lng: number };
    ne: { lat: number; lng: number };
  }) {
    const viewportFitsInFetchBounds =
      fetchSwLat !== null &&
      fetchSwLng !== null &&
      fetchNeLat !== null &&
      fetchNeLng !== null &&
      bounds.sw.lat >= fetchSwLat &&
      bounds.sw.lng >= fetchSwLng &&
      bounds.ne.lat <= fetchNeLat &&
      bounds.ne.lng <= fetchNeLng;

    if (viewportFitsInFetchBounds) return;

    // Pad the viewport by 50% in each direction so nearby pans are pre-loaded.
    const latSpan = bounds.ne.lat - bounds.sw.lat;
    const lngSpan = bounds.ne.lng - bounds.sw.lng;
    const latPad = latSpan * 0.5;
    const lngPad = lngSpan * 0.5;

    setFetchSwLat(Math.max(bounds.sw.lat - latPad, -85));
    setFetchSwLng(Math.max(bounds.sw.lng - lngPad, -180));
    setFetchNeLat(Math.min(bounds.ne.lat + latPad, 85));
    setFetchNeLng(Math.min(bounds.ne.lng + lngPad, 180));
  }

  const onMapBoundsChanged = useDebouncedCallback(
    async (bounds: {
      sw: { lat: number; lng: number };
      ne: { lat: number; lng: number };
    }) => {
      if (
        swLat === bounds.sw.lat &&
        swLng === bounds.sw.lng &&
        neLat === bounds.ne.lat &&
        neLng === bounds.ne.lng
      ) {
        return;
      }

      if (ignoreAreaFocusClearRef.current) {
        ignoreAreaFocusClearRef.current = false;
      } else if (areaFocusRef.current) {
        setAreaFocus(null);
      }

      setSwLat(bounds.sw.lat);
      setSwLng(bounds.sw.lng);
      setNeLat(bounds.ne.lat);
      setNeLng(bounds.ne.lng);
      setMapBounds(bounds);
      setCurrentZoom(mapRef.current?.getZoomLevel() ?? 12);
      updateFetchBoundsIfNeeded(bounds);

      if (shouldKeepLocation) {
        keepLocationCountRef.current = Math.max(
          keepLocationCountRef.current - 1,
          0,
        );
        if (keepLocationCountRef.current === 0) {
          setShouldKeepLocation(false);
        }
      } else {
        const coords = mapRef.current?.getCoords();
        const zoom = mapRef.current?.getZoomLevel();

        if (coords && zoom) {
          const params = new URLSearchParams(searchParams.toString());

          params.set(URL_KEYS.latitude, String(coords.lat));
          params.set(URL_KEYS.longitude, String(coords.lng));
          params.set(URL_KEYS.zoom, String(zoom));

          if (params.has(URL_KEYS.swLat)) {
            params.delete(URL_KEYS.swLat);
          }

          if (params.has(URL_KEYS.swLng)) {
            params.delete(URL_KEYS.swLng);
          }

          if (params.has(URL_KEYS.neLat)) {
            params.delete(URL_KEYS.neLat);
          }

          if (params.has(URL_KEYS.neLng)) {
            params.delete(URL_KEYS.neLng);
          }

          setFilters((prev) => {
            if (prev.location === null) return prev;
            return { ...prev, location: null };
          });
          window.history.replaceState(
            window.history.state ?? {},
            "",
            `/businesses?${params.toString()}`,
          );
        }
      }

      // Avoid auto-selecting a location on first paint. Infer only after users
      // actively move the map, or when an explicit location already exists.
      const shouldInferLocation = !shouldKeepLocation || !!filters.location;

      if (shouldInferLocation) {
        try {
          const nextLocation = await cachedEstimateLocationFromMap(
            mapRef.current,
            mapSearchHook,
            currentZoom,
            locationCacheRef.current,
          );
          setInferredLocation(nextLocation);
          if (nextLocation?.context.country?.code) {
            setCountryCode(nextLocation.context.country.code.toLowerCase());
          }
        } catch {
          setInferredLocation(null);
        }
      }
    },
    500,
  );

  async function getSearchData(): Promise<SearchData | null> {
    if (!mapRef.current) {
      return null;
    }

    let location = filters.location;

    if (!location) {
      try {
        location = await cachedEstimateLocationFromMap(mapRef.current, mapSearchHook, currentZoom, locationCacheRef.current);
      } catch (err) {
        // Ignore aborted location lookups caused by rapid map/filter updates.
        if (
          err instanceof Error &&
          (err.name === "AbortError" ||
            err.message.toLowerCase().includes("signal is aborted"))
        ) {
          return null;
        }
        return null;
      }
    }

    if (!location) {
      // Location can be missing briefly while map/search requests settle.
      return null;
    }

    const coords = mapRef.current.getBounds();

    return {
      businessTypeIds: filters.businessTypeIds,
      statuses: filters.statuses,
      priceMin: Number(filters.priceRange.min),
      priceMax: Number(filters.priceRange.max),
      revenueMin: Number(filters.revenueRange.min),
      revenueMax: Number(filters.revenueRange.max),
      profitMin: Number(filters.profitRange.min),
      profitMax: Number(filters.profitRange.max),
      sdeMin: Number(filters.sdeRange.min),
      sdeMax: Number(filters.sdeRange.max),
      sdeMultipleMin: filters.sdeMultipleRange.min,
      sdeMultipleMax: filters.sdeMultipleRange.max,
      revenueMultipleMin: filters.revenueMultipleRange.min,
      revenueMultipleMax: filters.revenueMultipleRange.max,
      downPaymentMin: Number(filters.downPaymentRange.min),
      downPaymentMax: Number(filters.downPaymentRange.max),
      establishedAfter: filters.establishmentAgeRange.min
        ? new Date(
            new Date().getFullYear() - filters.establishmentAgeRange.min,
            0,
            1,
          )
        : undefined,
      establishedBefore: filters.establishmentAgeRange.max
        ? new Date(
            new Date().getFullYear() - filters.establishmentAgeRange.max,
            0,
            1,
          )
        : undefined,
      ownerWorkedHoursMin: filters.ownerWorkedHoursRange.min,
      ownerWorkedHoursMax: filters.ownerWorkedHoursRange.max,
      employeesMin: filters.employeeCountRange.min,
      employeesMax: filters.employeeCountRange.max,
      propertyIncluded: filters.propertyIncluded,
      financingAvailable: filters.financingAvailable,
      sbaPrequalified: filters.sbaPrequalified,
      visaQualified: filters.visaQualified,
      includeMissingMultiples: filters.includeMissingMultiples,
      priceDirection: filters.priceDirection,
      priceChangedWithinDays: filters.priceChangedWithinDays,
      minPriceChangePercent: filters.minPriceChangePercent,
      countryName: location?.context.country?.name,
      countryCode: location?.context.country?.code,
      city: location?.context.place?.name,
      county: location?.context.district?.name,
      state: location?.context.region?.name,
      swLat: swLat ?? coords?.sw.lat,
      swLng: swLng ?? coords?.sw.lng,
      neLat: neLat ?? coords?.ne.lat,
      neLng: neLng ?? coords?.ne.lng,
    };
  }

  async function createSavedSearch() {
    if (!mapRef.current) {
      return;
    }

    if (!user) {
      return;
    }

    const searchData = await getSearchData();
    if (!searchData) {
      return;
    }

    try {
      await createSearchMutation.mutateAsync(searchData);
    } catch (err) {
      if (
        err instanceof Error &&
        (err.name === "AbortError" ||
          err.message.toLowerCase().includes("signal is aborted"))
      ) {
        return;
      }
      console.error(err);
    }
  }

  useEffect(() => {
    if (!coords || !mapRef.current) {
      return;
    }

    mapRef.current?.goToCoords(coords);
  }, [coords, mapRef]);

  useEffect(() => {
    if (!pathname.startsWith("/businesses/")) return;

    const locationPath = startingLocation
      ? routeLocationPath(startingLocation, startingCountryCode)
      : null;

    if (adoptedRoutePath.current === null) {
      adoptedRoutePath.current = pathname;
      return;
    }

    if (
      !shouldAdoptRouteLocation({
        pathname,
        previousPath: adoptedRoutePath.current,
        locationPath,
      }) ||
      !startingLocation
    ) {
      return;
    }

    adoptedRoutePath.current = pathname;
    setShouldKeepLocation(true);
    keepLocationCountRef.current = 3;
    isInitialLocationFromServer.current = true;
    setFilters((prev) => ({ ...prev, location: startingLocation }));

    const bounds = startingLocation.bbox
      ? getSafeBounds(startingLocation.bbox, startingLocation.coords)
      : null;
    if (!bounds) return;

    setSwLat(bounds.sw.lat);
    setSwLng(bounds.sw.lng);
    setNeLat(bounds.ne.lat);
    setNeLng(bounds.ne.lng);
    setMapBounds(bounds);
    const latSpan = bounds.ne.lat - bounds.sw.lat;
    const lngSpan = bounds.ne.lng - bounds.sw.lng;
    setFetchSwLat(Math.max(bounds.sw.lat - latSpan * 0.5, -85));
    setFetchSwLng(Math.max(bounds.sw.lng - lngSpan * 0.5, -180));
    setFetchNeLat(Math.min(bounds.ne.lat + latSpan * 0.5, 85));
    setFetchNeLng(Math.min(bounds.ne.lng + lngSpan * 0.5, 180));
  }, [pathname, startingLocation, startingCountryCode, setFilters, setMapBounds]);

  useEffect(() => {
    if (!filters.location) {
      return;
    }

    setShouldKeepLocation(true);
    keepLocationCountRef.current = 3;

    const isInitial = isInitialLocationFromServer.current;
    onLocationChange(filters.location, { animate: false });

    if (isInitial) {
      isInitialLocationFromServer.current = false;
      saveLocation(filters.location, countryCode);
      return;
    }

    const path = buildLocationUrlSegments(filters.location, countryCode);
    if (path) {
      const search = window.location.search;
      window.history.replaceState(window.history.state ?? {}, "", `/businesses/${path}${search}`);
    } else {
      const params = new URLSearchParams(window.location.search);
      const search = params.toString();
      window.history.replaceState(window.history.state ?? {}, "", `/businesses${search ? `?${search}` : ""}`);
    }
    saveLocation(filters.location, countryCode);
  }, [filters.location]);

  useEffect(() => {
    if (!resolved.current.location) return;

    const loc = resolved.current.location;
    setFilters((prev) => ({
      ...prev,
      location: loc,
    }));
  }, []);

  useEffect(() => {
    if (filters.location) {
      setInferredLocation(filters.location);
    }
  }, [filters.location]);

  useEffect(() => {
    if (!mapRef.current) {
      return;
    }

    const latStr = searchParams.get(URL_KEYS.latitude);
    const lngStr = searchParams.get(URL_KEYS.longitude);
    const zoomStr = searchParams.get(URL_KEYS.zoom);

    if (latStr && lngStr) {
      const lat = Number.parseFloat(latStr);
      const lng = Number.parseFloat(lngStr);
      const zoom = zoomStr ? Number.parseFloat(zoomStr) : Number.NaN;

      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        if (Number.isFinite(zoom)) {
          mapRef.current.goToCoords({ lat, lng }, zoom);
        } else {
          mapRef.current.goToCoords({ lat, lng });
        }
      }
    }

    const swLatStr = searchParams.get(URL_KEYS.swLat);
    const swLngStr = searchParams.get(URL_KEYS.swLng);
    const neLatStr = searchParams.get(URL_KEYS.neLat);
    const neLngStr = searchParams.get(URL_KEYS.neLng);

    if (swLatStr && swLngStr && neLatStr && neLngStr) {
      const swLat = Number.parseFloat(swLatStr);
      const swLng = Number.parseFloat(swLngStr);
      const neLat = Number.parseFloat(neLatStr);
      const neLng = Number.parseFloat(neLngStr);

      mapRef.current.setBounds(
        { sw: { lat: swLat, lng: swLng }, ne: { lat: neLat, lng: neLng } },
        { animate: !isBackNavigation.current },
      );
      setMapBounds({
        sw: { lat: swLat, lng: swLng },
        ne: { lat: neLat, lng: neLng },
      });
    }
  }, [mapRef, searchParams, setMapBounds]);

  const isScoutSetupActive =
    !!user?.isAgent &&
    searchParams.get("intent") === "scout" &&
    !filters.location;

  const hasSearchParamCoords =
    searchParams.has(URL_KEYS.latitude) || searchParams.has(URL_KEYS.swLat);

  const isLocationSetupNeeded =
    !resolved.current.location &&
    !filters.location &&
    !hasSearchParamCoords &&
    !isScoutSetupActive;

  async function resolveScoutCurrentLocation() {
    if (typeof window === "undefined") {
      return;
    }

    if (!navigator.geolocation) {
      setScoutSetupError(
        "Location is unavailable on this device. Search by city, state, or ZIP.",
      );
      return;
    }

    setIsLocatingUser(true);
    setScoutSetupError(null);

    try {
      const position = await new Promise<GeolocationPosition>(
        (resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 300000,
          });
        },
      );

      const coords = {
        lat: position.coords.latitude,
        lng: position.coords.longitude,
      };

      const reverse = await mapSearchHook.fromCoords(coords);
      const nextLocation =
        reverse.locations.find(
          (location) =>
            !!location.context.place ||
            !!location.context.district ||
            !!location.context.region,
        ) ?? reverse.locations[0];

      if (!nextLocation) {
        setScoutSetupError(
          "We couldn't resolve your location. Search by city, state, or ZIP.",
        );
        return;
      }

      setFilters((prev) => ({
        ...prev,
        location: nextLocation,
      }));
      setScoutSetupError(null);
    } catch (error) {
      if (
        typeof error === "object" &&
        error !== null &&
        "code" in error &&
        typeof (error as { code?: number }).code === "number" &&
        (error as { code: number }).code === 1
      ) {
        setScoutSetupError(
          "Location access was blocked. Search by city, state, or ZIP.",
        );
      } else {
        setScoutSetupError(
          "Couldn't access your location right now. Search by city, state, or ZIP.",
        );
      }
    } finally {
      setIsLocatingUser(false);
    }
  }

  // Keep shared bounds atom in sync even when bounds are updated
  // through state transitions outside the debounced map callback.
  useEffect(() => {
    if (swLat === null || swLng === null || neLat === null || neLng === null) {
      return;
    }

    setMapBounds({
      sw: { lat: swLat, lng: swLng },
      ne: { lat: neLat, lng: neLng },
    });
  }, [swLat, swLng, neLat, neLng, setMapBounds]);

  const debouncedSearchParamsUpdate = useDebouncedCallback(
    (
      pathname: string,
      currentParams: URLSearchParams,
      filters: BusinessFiltersType,
      sort?: string,
    ) => {
      const params = new URLSearchParams(currentParams.toString());
      if (sort && sort !== "recommended_desc") {
        params.set(URL_KEYS.sortBy, sort);
      } else {
        params.delete(URL_KEYS.sortBy);
      }
      updateSearchParams(pathname, params, filters);
    },
    100,
  );

  const boostedAttributes = useMemo<ListingInfoAttribute[]>(() => {
    const attributes: ListingInfoAttribute[] = [];

    if (filters.sdeRange.min || filters.sdeRange.max) {
      attributes.push("SDE");
    }

    if (filters.revenueRange.min || filters.revenueRange.max) {
      attributes.push("REVENUE");
    }

    if (filters.profitRange.min || filters.profitRange.max) {
      attributes.push("PROFIT");
    }

    if (filters.sbaPrequalified) {
      attributes.push("SBA_PREQUALIFIED");
    }

    if (filters.financingAvailable) {
      attributes.push("FINANCING_AVAILABLE");
    }

    if (filters.propertyIncluded) {
      attributes.push("PROPERTY_INCLUDED");
    }

    if (
      filters.ownerWorkedHoursRange.min ||
      filters.ownerWorkedHoursRange.max
    ) {
      attributes.push("OWNER_WEEKLY_HOURS");
    }

    return attributes;
  }, [filters]);

  // Reset fetch bounds, scroll position, and saved map bounds when filters
  // truly change (user interaction), not during mount-phase initializations.
  // Compares serialized filterParams so mount-phase effects that set the same
  // filters from URL search params don't trigger a destructive reset.
  useEffect(() => {
    const serialized = JSON.stringify(filterParams, (_key, value) =>
      typeof value === "bigint" ? value.toString() : value,
    );

    if (prevFilterParamsRef.current === null) {
      prevFilterParamsRef.current = serialized;
      return;
    }

    if (prevFilterParamsRef.current === serialized) {
      return;
    }

    prevFilterParamsRef.current = serialized;

    const SETTLING_MS = isBackNavigation.current ? 2000 : 500;
    if (
      backNavMountedAt.current &&
      Date.now() - backNavMountedAt.current < SETTLING_MS
    ) {
      return;
    }

    setBackNavCache(null);

    setFetchSwLat(null);
    setFetchSwLng(null);
    setFetchNeLat(null);
    setFetchNeLng(null);
    clearSavedPosition();
    try {
      sessionStorage.removeItem(MAP_BOUNDS_STORAGE_KEY);
      sessionStorage.removeItem(LISTINGS_CACHE_KEY);
    } catch {
      // ignore
    }
  }, [filterParams]);

  useEffect(() => {
    // Re-expand fetch bounds after filter reset, using current viewport.
    if (
      fetchSwLat === null &&
      swLat !== null &&
      swLng !== null &&
      neLat !== null &&
      neLng !== null
    ) {
      updateFetchBoundsIfNeeded({
        sw: { lat: swLat, lng: swLng },
        ne: { lat: neLat, lng: neLng },
      });
    }
  }, [fetchSwLat, swLat, swLng, neLat, neLng]);

  useEffect(() => {
    if (
      swLat &&
      swLng &&
      neLat &&
      neLng &&
      fetchSwLat &&
      fetchSwLng &&
      fetchNeLat &&
      fetchNeLng
    ) {
      saveMapBounds({
        sw: { lat: swLat, lng: swLng },
        ne: { lat: neLat, lng: neLng },
        fetchSw: { lat: fetchSwLat, lng: fetchSwLng },
        fetchNe: { lat: fetchNeLat, lng: fetchNeLng },
      });
    }
  }, [
    swLat,
    swLng,
    neLat,
    neLng,
    fetchSwLat,
    fetchSwLng,
    fetchNeLat,
    fetchNeLng,
  ]);

  useEffect(() => {
    if (!data || !countData || isFetching) return;
    const hasListings = data.pages.some((p) => p.listings.length > 0);
    if (!hasListings) return;
    saveListingsCache(data.pages, countData.count);
  }, [data, countData, isFetching]);

  // Once React Query has real data, drop the back-nav cache overlay.
  useEffect(() => {
    if (!backNavCache) return;
    if (data && !isLoading && countData) {
      setBackNavCache(null);
    }
  }, [backNavCache, data, isLoading, countData]);

  useEffect(() => {
    const params = new URLSearchParams(searchParams.toString());
    debouncedSearchParamsUpdate(pathname, params, filters, orderByValue);

    if (!params.has("searchId")) {
      createSavedSearch();
    }
  }, [filters, orderByValue]);

  useEffect(() => {
    const params = new URLSearchParams(searchParams.toString());
    const filters = readFromSearchParams(params);

    setFilters((prev) => ({
      ...prev,
      ...filters,
    }));

    const sortParam = params.get(URL_KEYS.sortBy);
    if (
      sortParam &&
      (ORDER_BY_VALUES as readonly string[]).includes(sortParam)
    ) {
      setOrderByValue(sortParam as OrderByValue);
    }
  }, []);

  const displayLocation = filters.location ?? inferredLocation ?? undefined;

  // When the back-nav cache is active, use it as the effective data source
  // so listings render immediately regardless of React Query state.
  const effectiveData = backNavCache
    ? { pages: backNavCache.pages }
    : data;
  const effectiveCountData = backNavCache
    ? { count: backNavCache.count }
    : countData;
  const effectiveIsLoading = backNavCache ? false : isLoading;
  const effectiveCountDataLoading = backNavCache ? false : countDataLoading;
  const effectiveHasNextPage = backNavCache ? false : hasNextPage;

  const isResultsUpdating =
    !backNavCache &&
    (isMapFetching || (isFetching && !isFetchingNextPage)) &&
    !effectiveIsLoading &&
    !effectiveCountDataLoading;
  const isWaitingForBounds =
    !hasViewportBounds && !backNavCache && !isLocationSetupNeeded && !isScoutSetupActive;

  if (isLocationSetupNeeded) {
    const popularLocations = [
      { label: "Florida", href: "/businesses/us/florida" },
      { label: "Texas", href: "/businesses/us/texas" },
      { label: "California", href: "/businesses/us/california" },
      { label: "New York", href: "/businesses/us/new-york" },
      { label: "Illinois", href: "/businesses/us/illinois" },
      { label: "Canada", href: "/businesses/ca" },
      { label: "New Zealand", href: "/businesses/nz" },
    ];

    return (
      <div className="flex min-h-[calc(100dvh-4rem)] items-center justify-center bg-secondary p-4 md:p-8">
        <div className="w-full max-w-2xl rounded-2xl border border-border bg-card p-5 shadow-[0_14px_34px_rgba(0,0,0,0.08)] md:p-7">
          <div className="mb-5">
            <p className="text-[12px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
              Get started
            </p>
            <h1 className="mt-2 text-[28px] leading-tight font-semibold tracking-[-0.02em] text-foreground">
              Where are you looking for businesses?
            </h1>
            <p className="mt-2 text-[15px] text-muted-foreground">
              Start with your area, or search a city, state, or ZIP.
            </p>
          </div>

          <div className="space-y-3">
            <Button
              type="button"
              onClick={() => void resolveScoutCurrentLocation()}
              className="h-11 w-full"
              disabled={isLocatingUser}
            >
              {isLocatingUser ? "Locating..." : "Use my location"}
            </Button>
            <LocationSearchBox isLoading={isLocatingUser} />
          </div>

          {scoutSetupError ? (
            <p className="mt-3 text-sm text-[destructive]">{scoutSetupError}</p>
          ) : null}

          <div className="mt-5 border-t border-border pt-4">
            <p className="mb-2.5 text-[12px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
              Popular locations
            </p>
            <div className="flex flex-wrap gap-2">
              {popularLocations.map((loc) => (
                <Link
                  key={loc.href}
                  href={loc.href}
                  className="inline-flex items-center gap-1.5 rounded-full border border-border bg-secondary px-3.5 py-1.5 text-[13px] font-medium text-foreground transition-all duration-200 hover:-translate-y-px hover:border-border hover:shadow-[0_4px_12px_rgba(0,0,0,0.06)]"
                >
                  <MapPinIcon className="h-3.5 w-3.5 text-muted-foreground" />
                  {loc.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isScoutSetupActive) {
    return (
      <div className="flex min-h-[calc(100dvh-4rem)] items-center justify-center bg-secondary p-4 md:p-8">
        <div className="w-full max-w-2xl rounded-2xl border border-border bg-card p-5 shadow-[0_14px_34px_rgba(0,0,0,0.08)] md:p-7">
          <div className="mb-5">
            <p className="text-[12px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
              Scout setup
            </p>
            <h1 className="mt-2 text-[28px] leading-tight font-semibold tracking-[-0.02em] text-foreground">
              Where are you scouting for clients?
            </h1>
            <p className="mt-2 text-[15px] text-muted-foreground">
              Start with your area, or search a city, state, or ZIP.
            </p>
          </div>

          <div className="space-y-3">
            <Button
              type="button"
              onClick={() => void resolveScoutCurrentLocation()}
              className="h-11 w-full"
              disabled={isLocatingUser}
            >
              {isLocatingUser ? "Locating..." : "Use my location"}
            </Button>
            <LocationSearchBox isLoading={isLocatingUser} />
          </div>

          {scoutSetupError ? (
            <p className="mt-3 text-sm text-[destructive]">{scoutSetupError}</p>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <>
      {!effectiveIsLoading && effectiveData && filters.location && (
        <>
          <BusinessesJsonLD
            listings={effectiveData.pages.flatMap((page) => page.listings)}
            location={filters.location}
          />
          <BusinessesBreadcrumbsJsonLD
            countryCode={countryCode}
            location={filters.location}
          />
        </>
      )}

      <div className="flex flex-col h-full overflow-x-hidden">
        <BusinessFilters
          getSearchData={getSearchData}
          sortOptions={ORDER_BY_VALUES as unknown as readonly string[]}
          sortValue={orderByValue}
          onSortValueChange={(val) => setOrderByValue(val as OrderByValue)}
        />
        <BusinessesMobileHeader
          currentView={searchView}
          onChangeView={setSearchView}
          sortOptions={ORDER_BY_VALUES}
          sortValue={orderByValue}
          onSortValueChange={setOrderByValue}
          isLoading={effectiveIsLoading}
          isLocationUpdating={isFetching && !isFetchingNextPage}
          collapsed={hideMobileHeaderOnScroll}
        />

        {!hideMobileHeaderOnScroll &&
          !effectiveCountDataLoading &&
          effectiveCountData &&
          effectiveCountData.count > 0 && (
            <SaveSearchBanner
              count={effectiveCountData.count}
              getSearchData={getSearchData}
            />
          )}

        <div
          className={cn(
            "flex-1 h-full w-[200vw] grid grid-cols-2 overflow-hidden transition-transform duration-300 ease-out xl:w-auto xl:grid-cols-[60%_40%] xl:transition-none",
            {
              "-translate-x-1/2 xl:translate-x-0": searchView === "map",
            },
          )}
        >
          <div
            className="flex h-full w-screen flex-col gap-4 overflow-y-auto bg-card p-5 xl:w-auto"
            ref={mergedListRef}
            onScroll={handleListScroll}
          >
            {(effectiveCountDataLoading || isWaitingForBounds || !!effectiveCountData) && (
              <div className="rounded-xl border border-border/80 bg-card/90 p-4 shadow-[0_1px_0_rgba(255,255,255,0.92)_inset,0_8px_24px_rgba(0,0,0,0.08)] backdrop-blur-md supports-[backdrop-filter]:bg-card/72">
                <BusinessListBreadcrumbs
                  countryCode={countryCode}
                  location={displayLocation}
                  maxDepth={
                    currentZoom < 5 ? 1 : currentZoom < 9 ? 2 : undefined
                  }
                />

                {isWaitingForBounds && (
                  <div className="mt-2 flex items-center gap-1.5">
                    <LoaderCircleIcon className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">
                      {t("common.loading")}
                    </span>
                  </div>
                )}

                {!isWaitingForBounds && !!effectiveCountData && effectiveCountData.count > 0 && (
                  <div className="mt-2 flex items-center justify-between">
                    <H1 className="text-base xl:text-base">
                      {displayLocation ? (
                        <>
                          <span className="font-data">
                            {effectiveCountData.count}
                          </span>{" "}
                          {t("saas.search.countLabel", {
                            count: effectiveCountData.count,
                          })}{" "}
                          {t("saas.search.proximity.in")}{" "}
                          {filters.location
                            ? formatLocationWithFallback(displayLocation)
                            : formatLocationForZoom(
                                displayLocation,
                                currentZoom,
                              )}
                        </>
                      ) : (
                        t("saas.search.title", {
                          count: effectiveCountData.count,
                          proximity: t("saas.search.proximity.near"),
                          location: t("saas.search.yourLocation"),
                        })
                      )}
                    </H1>

                    {isResultsUpdating && (
                      <div
                        className="ml-3 inline-flex items-center gap-1.5 rounded-full bg-card/90 px-3 py-1.5 text-xs font-medium text-muted-foreground shadow-sm backdrop-blur-sm"
                        aria-live="polite"
                      >
                        <LoaderCircleIcon className="h-3.5 w-3.5 animate-spin" />
                        {t("common.updating")}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {!isWaitingForBounds &&
              !effectiveIsLoading &&
              !effectiveCountDataLoading &&
              effectiveCountData &&
              effectiveCountData.count === 0 &&
              effectiveData &&
              (effectiveData.pages.length === 0 ||
                effectiveData.pages[0].listings.length === 0) && (
                <BusinessListZeroDataState mapRef={mapRef} />
              )}

            {!isWaitingForBounds &&
              !effectiveIsLoading &&
              effectiveData &&
              effectiveCountData &&
              effectiveCountData.count > 0 && (
                <div
                  className={cn(
                    "grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 transition-opacity duration-200",
                    isResultsUpdating && "opacity-60 pointer-events-none",
                  )}
                >
                  {areaFocus &&
                    !isResultsUpdating &&
                    effectiveData.pages.every((page) => page.listings.length === 0) && (
                      <div className="col-span-full space-y-2 text-sm text-muted-foreground">
                        <p>
                          No listings in {areaFocus.name} without an exact
                          address.
                        </p>
                        <button
                          type="button"
                          className="min-h-11 font-medium text-foreground underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground"
                          onClick={() => setAreaFocus(null)}
                        >
                          Show all results
                        </button>
                      </div>
                    )}
                  {effectiveData.pages
                    .flatMap((page) => page.listings)
                    .map((listing: any, index: number, listings: any[]) => {
                      const showAreaDivider =
                        listing.locationTier === "AREA" &&
                        listings.findIndex((item) => item.locationTier === "AREA") ===
                          index;
                      return (
                        <Fragment key={listing.slug ?? `listing-${listing.id}`}>
                          {showAreaDivider && (
                            <div
                              ref={areaSectionRef}
                              className="col-span-full flex items-center justify-between gap-3 border-t border-border pt-4 text-sm text-muted-foreground"
                            >
                              <span>
                                {areaFocus
                                  ? `Somewhere in ${areaFocus.name}`
                                  : "Somewhere in this area"}
                              </span>
                              {areaFocus ? (
                                <button
                                  type="button"
                                  className="min-h-11 shrink-0 font-medium text-foreground underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground"
                                  onClick={() => setAreaFocus(null)}
                                >
                                  Show all results
                                </button>
                              ) : null}
                            </div>
                          )}
                          <Link
                            href={buildListingUrl(listing)}
                            className="flex flex-col focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground rounded-xl"
                            target={isDesktopLayout ? "_blank" : undefined}
                            rel={isDesktopLayout ? "noreferrer" : undefined}
                            prefetch={false}
                            onClick={() => {
                              saveScrollNow();
                              if (!isDesktopLayout) {
                                setNavigatingSlug(listing.slug ?? listing.id);
                              }
                            }}
                          >
                            <ListingPreview
                              listing={listing}
                              boostedAttributes={boostedAttributes}
                              navigating={
                                navigatingSlug === (listing.slug ?? listing.id)
                              }
                            />
                          </Link>
                        </Fragment>
                      );
                    })}
                </div>
              )}

            {(isWaitingForBounds || effectiveIsLoading || effectiveHasNextPage) && (
              <div
                className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3"
                ref={isWaitingForBounds ? undefined : infiniteRef}
              >
                <BusinessEntrySkeleton />
                <BusinessEntrySkeleton />
                <BusinessEntrySkeleton />
                <BusinessEntrySkeleton />
                <BusinessEntrySkeleton />
                <BusinessEntrySkeleton />
              </div>
            )}

            {!effectiveIsLoading &&
              effectiveData &&
              !effectiveHasNextPage &&
              effectiveCountData &&
              effectiveCountData.count > 0 && (
                <BusinessListNearbySection
                  excludeIds={effectiveData.pages
                    .flatMap((p: any) => p.listings)
                    .map((l: any) => l.id)}
                />
              )}
          </div>

          <div className="relative h-full w-screen xl:w-auto">
            <MapUnpinnedControl
              areaCount={areaCount}
              isUpdating={isMapFetching}
              onShow={showAreaListings}
            />
            <MapComponent
              mapRef={mapRef}
              token={process.env.NEXT_PUBLIC_MAP_TOKEN as string}
              data={mapPoints}
              coordsGetter={(point) => ({
                lat: point.lat,
                lng: point.lng,
              })}
              keyGetter={(point) =>
                isAreaMapPoint(point)
                  ? `area-${point.area.subdivisionId}`
                  : point.isCluster
                    ? `cluster-${point.clusterId}`
                    : point.item.id
              }
              settings={{
                map: {
                  center:
                    coords ??
                    filters.location?.coords ??
                    resolved.current.location?.coords,
                  bounds: resolved.current.location?.bbox
                    ? (getSafeBounds(
                        resolved.current.location.bbox,
                        resolved.current.location.coords,
                      ) ?? undefined)
                    : undefined,
                },
                popup: {
                  closeButton: false,
                  closeOnClick: false,
                  closeOnMove: false,
                  maxWidth: "none",
                  offset: 10,
                },
              }}
              renderMarker={({ item: point, markerElementRef, isActive }) => {
                if (isAreaMapPoint(point)) {
                  return (
                    <MapAreaMarker
                      markerElementRef={markerElementRef}
                      name={point.area.name}
                      count={point.area.count}
                      areaId={point.area.subdivisionId}
                      isHighlighted={
                        isActive ||
                        (!isNotMobile &&
                          selectedAreaId === point.area.subdivisionId)
                      }
                      isActive={isActive}
                      onHover={onAreaHover}
                      onClick={() => {
                        if (isNotMobile) return;
                        setSelectedMapListingSlug(null);
                        setSelectedAreaId((prev) =>
                          prev === point.area.subdivisionId
                            ? null
                            : point.area.subdivisionId,
                        );
                      }}
                    />
                  );
                }

                const isMobileSelected =
                  !isNotMobile &&
                  !point.isCluster &&
                  point.item.slug === selectedMapListingSlug;
                const isHighlighted = isActive || isMobileSelected;

                return (
                  // biome-ignore lint/a11y/useKeyWithClickEvents: Map markers are pointer-only interactive elements managed by Mapbox
                  <div
                    ref={markerElementRef}
                    onClick={() => {
                      if (point.isCluster) {
                        const expansionZoom = getClusterExpansionZoom(
                          point.clusterId,
                        );
                        mapRef.current?.goToCoords(
                          { lat: point.lat, lng: point.lng },
                          Math.min(expansionZoom, 20),
                        );
                      } else if (point.item.slug && !isNotMobile) {
                        setSelectedAreaId(null);
                        setSelectedMapListingSlug((prev) =>
                          prev === point.item.slug ? null : point.item.slug,
                        );
                      }
                    }}
                  >
                    <ListingMapMarker point={point} isHighlighted={isHighlighted} />
                  </div>
                );
              }}
              renderPopup={
                isNotMobile
                  ? ({ item: point, isActive }) =>
                      isAreaMapPoint(point) ? (
                        <MapAreaPopup
                          area={point.area}
                          isActive={isActive}
                          boostedAttributes={boostedAttributes}
                          onShowListings={() =>
                            focusAreaListings({
                              subdivisionId: point.area.subdivisionId,
                              name: point.area.name,
                            })
                          }
                        />
                      ) : (
                        <ListingMapPopup
                          point={point}
                          isActive={isActive}
                          boostedAttributes={boostedAttributes}
                        />
                      )
                  : undefined
              }
              onMapLoaded={(map) => {
                setAreaShadeMap(map.getRawClient() as AreaShadeMap);
              }}
              onMapBoundsChanged={onMapBoundsChanged}
              onCoordsClick={
                !isNotMobile
                  ? () => {
                      setSelectedMapListingSlug(null);
                      setSelectedAreaId(null);
                    }
                  : undefined
              }
              className="w-full h-full"
            />

            {/* Mobile bottom card — appears when a marker is tapped */}
            {!isNotMobile && selectedMapListingSlug && (
              <div
                className="absolute left-3 right-3 z-20 animate-in slide-in-from-bottom-4 fade-in duration-200"
                style={{
                  bottom: "calc(env(safe-area-inset-bottom, 0px) + 1rem)",
                }}
              >
                <div className="relative rounded-xl bg-card shadow-[0_-2px_20px_rgba(0,0,0,0.14)] border border-border/50 overflow-hidden">
                  <button
                    type="button"
                    className="absolute top-2 right-2 z-10 flex h-7 w-7 items-center justify-center rounded-full bg-black/40 text-white hover:bg-black/60 transition-colors"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setSelectedMapListingSlug(null);
                    }}
                  >
                    <XIcon className="h-3.5 w-3.5" />
                  </button>

                  <Link
                    href={`/business/${selectedMapListingSlug}`}
                    className="block focus-visible:outline-none"
                    prefetch={false}
                  >
                    <MapListingCard
                      slug={selectedMapListingSlug}
                      enabled
                      horizontal
                      boostedAttributes={boostedAttributes}
                    />
                  </Link>
                </div>
              </div>
            )}

            {!isNotMobile && selectedArea && (
              <div
                className="absolute left-3 right-3 z-20 animate-in slide-in-from-bottom-4 fade-in duration-200"
                style={{
                  bottom: "calc(env(safe-area-inset-bottom, 0px) + 1rem)",
                }}
              >
                <div className="relative overflow-hidden rounded-xl border border-border/50 bg-card shadow-[0_-2px_20px_rgba(0,0,0,0.14)]">
                  <button
                    type="button"
                    className="absolute top-2 right-2 z-10 flex h-7 w-7 items-center justify-center rounded-full bg-black/40 text-white hover:bg-black/60 transition-colors"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setSelectedAreaId(null);
                    }}
                    aria-label="Close"
                  >
                    <XIcon className="h-3.5 w-3.5" />
                  </button>
                  <MapAreaPopup
                    area={selectedArea}
                    isActive
                    horizontal
                    boostedAttributes={boostedAttributes}
                    onShowListings={() => {
                      setSelectedAreaId(null);
                      focusAreaListings({
                        subdivisionId: selectedArea.subdivisionId,
                        name: selectedArea.name,
                      });
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

BusinessesView.displayName = "BusinessesView";
